/**
 * Datacenter Density Control Panel
 *
 * Provides UI controls for datacenter density limits and displays validation warnings.
 * Integrates with DensityValidator from the power module.
 */

import { useState, useEffect } from 'react';
import {
  validateRackDensity,
  recommendDatacenterTier,
  DATACENTER_TIERS,
  type DensityLimits,
  type RackDensityMetrics,
  type DensityViolation,
} from '../../power/DensityValidator';
import { useStore } from '../../store/store';

interface DensityControlPanelProps {
  className?: string;
}

export function DensityControlPanel({ className = '' }: DensityControlPanelProps) {
  const configuration = useStore((state) => state.configuration);
  const [selectedTier, setSelectedTier] = useState<keyof typeof DATACENTER_TIERS>('high-density');
  const [customLimits, setCustomLimits] = useState<DensityLimits | null>(null);
  const [useCustom, setUseCustom] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);

  const { rack, power, server } = configuration;

  // Calculate current rack density metrics
  const calculateMetrics = (): RackDensityMetrics => {
    const powerPerRackKw = rack.serversPerRack * power.powerPerNodeKw;
    const weightPerRackLbs = rack.serversPerRack * 300; // Estimate 300 lbs per node
    const rackUnitsUsed = rack.serversPerRack * server.rackUnits;
    const coolingRequiredBtuPerHour = powerPerRackKw * 3412; // 1 kW = 3412 BTU/hr
    const floorSpacePerRackSqFt = 7; // Typical rack footprint

    return {
      powerPerRackKw,
      weightPerRackLbs,
      rackUnitsUsed,
      coolingRequiredBtuPerHour,
      floorSpacePerRackSqFt,
    };
  };

  // Validate density on configuration change
  useEffect(() => {
    const metrics = calculateMetrics();
    const limits = useCustom && customLimits ? customLimits : DATACENTER_TIERS[selectedTier];
    const result = validateRackDensity(metrics, limits);
    setValidationResult(result);

    // Auto-recommend tier if not using custom limits
    if (!useCustom) {
      const recommendation = recommendDatacenterTier(metrics.powerPerRackKw);
      if (recommendation.recommendedTier !== selectedTier) {
        console.log(
          `Recommended tier: ${recommendation.recommendedTier} - ${recommendation.reason}`
        );
      }
    }
  }, [configuration, selectedTier, customLimits, useCustom]);

  // Initialize custom limits from selected tier
  const initializeCustomLimits = () => {
    setCustomLimits({ ...DATACENTER_TIERS[selectedTier] });
    setUseCustom(true);
  };

  const renderViolation = (violation: DensityViolation) => {
    const bgColor = violation.severity === 'error' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200';
    const textColor = violation.severity === 'error' ? 'text-red-900' : 'text-yellow-900';
    const icon = violation.severity === 'error' ? '❌' : '⚠️';

    return (
      <div key={violation.type} className={`p-3 border rounded ${bgColor} ${textColor} text-sm mb-2`}>
        <div className="flex items-start gap-2">
          <span className="text-base">{icon}</span>
          <div className="flex-1">
            <div className="font-semibold">{violation.message}</div>
            <div className="text-xs mt-1 opacity-80">
              Utilization: {violation.utilizationPercent.toFixed(0)}% ({violation.currentValue.toFixed(1)}{' '}
              {violation.unit} / {violation.limitValue.toFixed(1)} {violation.unit} limit)
            </div>
            <div className="text-xs mt-1 italic">{violation.recommendation}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderUtilizationBar = (label: string, utilization: number, unit: string = '%') => {
    const percentage = Math.min(utilization * 100, 100);
    const color =
      percentage > 100
        ? 'bg-red-500'
        : percentage > 85
        ? 'bg-yellow-500'
        : percentage > 70
        ? 'bg-blue-500'
        : 'bg-green-500';

    return (
      <div className="mb-3">
        <div className="flex justify-between text-xs font-medium text-gray-700 mb-1">
          <span>{label}</span>
          <span>
            {percentage.toFixed(0)}
            {unit}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className={`h-2 rounded-full ${color} transition-all`} style={{ width: `${percentage}%` }}></div>
        </div>
      </div>
    );
  };

  const metrics = calculateMetrics();

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 ${className}`}>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Datacenter Density</h3>

      {/* Tier Selection */}
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Datacenter Tier</label>
        <select
          value={selectedTier}
          onChange={(e) => {
            setSelectedTier(e.target.value as keyof typeof DATACENTER_TIERS);
            setUseCustom(false);
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
          disabled={useCustom}
        >
          <option value="standard">Standard (≤15 kW/rack)</option>
          <option value="high-density">High Density (≤30 kW/rack)</option>
          <option value="liquid-cooled">Liquid Cooled (≤100 kW/rack)</option>
          <option value="hyperscale-liquid">Hyperscale Liquid (≤250 kW/rack)</option>
        </select>
      </div>

      {/* Custom Limits Toggle */}
      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={useCustom}
            onChange={(e) => {
              if (e.target.checked) {
                initializeCustomLimits();
              } else {
                setUseCustom(false);
                setCustomLimits(null);
              }
            }}
            className="rounded"
          />
          Use Custom Limits
        </label>
      </div>

      {/* Custom Limits Inputs */}
      {useCustom && customLimits && (
        <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
          <div className="text-xs font-semibold text-gray-600 uppercase mb-2">Custom Limits</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Max Power (kW/rack)</label>
              <input
                type="number"
                value={customLimits.maxPowerPerRackKw}
                onChange={(e) =>
                  setCustomLimits({
                    ...customLimits,
                    maxPowerPerRackKw: Number(e.target.value),
                  })
                }
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                min={1}
                step={1}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Max Weight (lbs/rack)</label>
              <input
                type="number"
                value={customLimits.maxWeightPerRackLbs}
                onChange={(e) =>
                  setCustomLimits({
                    ...customLimits,
                    maxWeightPerRackLbs: Number(e.target.value),
                  })
                }
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                min={100}
                step={100}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Floor Loading (lbs/sq ft)</label>
              <input
                type="number"
                value={customLimits.floorLoadingLimitLbsPerSqFt}
                onChange={(e) =>
                  setCustomLimits({
                    ...customLimits,
                    floorLoadingLimitLbsPerSqFt: Number(e.target.value),
                  })
                }
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                min={50}
                step={10}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Max Rack Units</label>
              <input
                type="number"
                value={customLimits.maxRackUnits}
                onChange={(e) =>
                  setCustomLimits({
                    ...customLimits,
                    maxRackUnits: Number(e.target.value),
                  })
                }
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                min={42}
                step={1}
              />
            </div>
          </div>
        </div>
      )}

      {/* Current Metrics */}
      <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
        <div className="text-xs font-semibold text-blue-900 uppercase mb-2">Current Density</div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-gray-600">Power per Rack:</span>
            <span className="font-semibold text-gray-900 ml-2">{metrics.powerPerRackKw.toFixed(1)} kW</span>
          </div>
          <div>
            <span className="text-gray-600">Weight per Rack:</span>
            <span className="font-semibold text-gray-900 ml-2">{metrics.weightPerRackLbs.toFixed(0)} lbs</span>
          </div>
          <div>
            <span className="text-gray-600">Rack Units Used:</span>
            <span className="font-semibold text-gray-900 ml-2">{metrics.rackUnitsUsed} RU</span>
          </div>
          <div>
            <span className="text-gray-600">Cooling Required:</span>
            <span className="font-semibold text-gray-900 ml-2">
              {(metrics.coolingRequiredBtuPerHour / 1000).toFixed(0)}k BTU/hr
            </span>
          </div>
        </div>
      </div>

      {/* Utilization Bars */}
      {validationResult && (
        <div className="mb-4">
          <div className="text-xs font-semibold text-gray-600 uppercase mb-2">Utilization</div>
          {renderUtilizationBar('Power', validationResult.utilization.powerUtilization)}
          {renderUtilizationBar('Weight', validationResult.utilization.weightUtilization)}
          {renderUtilizationBar('Rack Space', validationResult.utilization.spaceUtilization)}
          {renderUtilizationBar('Cooling', validationResult.utilization.coolingUtilization)}
        </div>
      )}

      {/* Violations */}
      {validationResult && validationResult.violations.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold text-gray-600 uppercase mb-2">Density Issues</div>
          {validationResult.violations.map(renderViolation)}
        </div>
      )}

      {/* Success Message */}
      {validationResult && validationResult.valid && validationResult.violations.length === 0 && (
        <div className="p-3 bg-green-50 border border-green-200 rounded text-green-900 text-sm">
          ✓ All density constraints are within acceptable limits
        </div>
      )}
    </div>
  );
}
